import { now } from '../Clock.ts';
import type { StimulusRequest } from './Stimulator.ts';

/**
 * Operator-defined limits on stimulation. The software layer rejects anything
 * that violates them, before the request reaches any device.
 *
 * These are a backstop, not a substitute for the hardware's own limits. They
 * exist so that a bug in the controller -- an oscillating decision rule, a
 * runaway loop, a mis-set latency causing the bot to spam requests -- cannot
 * turn into a burst of stimulation delivered to a person.
 */
export interface SafetyLimits {
  /** Refractory period: minimum gap between two stimulation onsets, ms. */
  minIntervalMs: number;
  /** Hard cap on stimulation onsets in any rolling 1 s window. */
  maxPerSecond: number;
  /** Hard cap on stimulation onsets in any rolling 60 s window. */
  maxPerMinute: number;
  /** Longest single pulse the software will ever ask for, ms. */
  maxDurationMs: number;
  /** Total stimulations allowed before the operator must re-arm. */
  maxSessionStimuli: number;
  /** Wall-clock limit on one armed period, ms. Auto-disarms on expiry. */
  maxArmedDurationMs: number;
}

/**
 * Conservative defaults.
 *
 * `minIntervalMs: 250` is the important one. The bot's own refractory period is
 * 40 ms, which is fine for a keyboard and completely inappropriate for a person:
 * it would ask for 25 stimulations a second. This limit is what makes the
 * bot's intent rate and the participant's stimulation rate different numbers,
 * and the gap between them is itself an experimental observable.
 */
export const DEFAULT_SAFETY_LIMITS: SafetyLimits = {
  minIntervalMs: 250,
  maxPerSecond: 4,
  maxPerMinute: 120,
  maxDurationMs: 200,
  maxSessionStimuli: 2000,
  maxArmedDurationMs: 20 * 60 * 1000,
};

/**
 * Limits used when the stimulator is simulated.
 *
 * The conservative defaults above exist to protect a person. A
 * `SimulatedHumanStimulator` is not a person, and applying a 250 ms refractory
 * to it means the rate limiter -- not the response model under study -- is what
 * limits performance, which makes the delay comparisons uninterpretable.
 *
 * The simulated participant has its own `refractoryPeriodMs`, and that is the
 * constraint that should bind in simulation. Set
 * `enforceSafetyLimitsInSimulation` to study the rate limiter itself.
 */
export const SIMULATED_SAFETY_LIMITS: SafetyLimits = {
  ...DEFAULT_SAFETY_LIMITS,
  minIntervalMs: 40,
  maxPerSecond: 25,
  maxPerMinute: 1500,
  maxSessionStimuli: 100_000,
};

export type GateDecision =
  | { allowed: true; durationMs: number }
  | { allowed: false; reason: string };

export interface SafetyState {
  armed: boolean;
  emergencyStopped: boolean;
  armedAt: number | null;
  sessionStimuli: number;
  lastStimulusAt: number | null;
  inLastSecond: number;
  inLastMinute: number;
}

/**
 * Rate limiting, refractory enforcement, arming, and emergency stop.
 *
 * Deliberately separate from `Stimulator`: the same policy applies to a mock,
 * to a simulated human, and to real hardware, and it must not be possible for
 * a new adapter to accidentally not have it. `StimulationService` owns one gate
 * and every request goes through it.
 */
export class SafetyGate {
  limits: SafetyLimits;

  private armed = false;
  private emergencyStopped = false;
  private armedAt: number | null = null;
  private sessionStimuli = 0;
  private onsets: number[] = [];
  private listeners = new Set<(state: SafetyState) => void>();

  constructor(limits: Partial<SafetyLimits> = {}) {
    this.limits = { ...DEFAULT_SAFETY_LIMITS, ...limits };
  }

  onChange(fn: (state: SafetyState) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  // ------------------------------------------------------------------ arming

  arm(nowMs = now()): void {
    if (this.emergencyStopped) {
      throw new Error('Emergency stop is latched. Clear it explicitly before arming.');
    }
    this.armed = true;
    this.armedAt = nowMs;
    this.notify();
  }

  disarm(): void {
    this.armed = false;
    this.armedAt = null;
    this.notify();
  }

  /**
   * Latch the emergency stop. Disarms, and refuses to re-arm until explicitly
   * cleared by an operator action. Idempotent and never throws.
   */
  emergencyStop(): void {
    this.emergencyStopped = true;
    this.armed = false;
    this.armedAt = null;
    this.notify();
  }

  clearEmergencyStop(): void {
    this.emergencyStopped = false;
    this.notify();
  }

  /** Reset per-session counters. Does not arm and does not clear a stop. */
  resetSession(): void {
    this.sessionStimuli = 0;
    this.onsets = [];
    this.notify();
  }

  get isArmed(): boolean {
    return this.armed;
  }

  get isEmergencyStopped(): boolean {
    return this.emergencyStopped;
  }

  // ------------------------------------------------------------------- check

  /**
   * Evaluate a request against every limit.
   *
   * Returns the (possibly clamped) duration to use, or a refusal reason. The
   * caller must log the refusal -- a silently dropped stimulation request would
   * show up in analysis as a missed human response, which is a different thing
   * entirely.
   */
  check(request: StimulusRequest, nowMs = now()): GateDecision {
    if (this.emergencyStopped) return deny('emergency stop latched');
    if (!this.armed) return deny('not armed');

    if (this.armedAt !== null && nowMs - this.armedAt > this.limits.maxArmedDurationMs) {
      this.disarm();
      return deny('armed-period time limit reached; re-arm to continue');
    }

    this.prune(nowMs);

    const last = this.onsets[this.onsets.length - 1];
    if (last !== undefined && nowMs - last < this.limits.minIntervalMs) {
      return deny(
        `refractory: ${Math.round(nowMs - last)} ms since last stimulus, ` +
          `minimum ${this.limits.minIntervalMs} ms`,
      );
    }
    if (this.countSince(nowMs - 1000) >= this.limits.maxPerSecond) {
      return deny(`rate limit: ${this.limits.maxPerSecond}/s`);
    }
    if (this.countSince(nowMs - 60_000) >= this.limits.maxPerMinute) {
      return deny(`rate limit: ${this.limits.maxPerMinute}/min`);
    }
    if (this.sessionStimuli >= this.limits.maxSessionStimuli) {
      return deny(`session limit: ${this.limits.maxSessionStimuli} stimuli`);
    }
    if (!(request.durationMs > 0)) {
      return deny('invalid duration');
    }

    return { allowed: true, durationMs: Math.min(request.durationMs, this.limits.maxDurationMs) };
  }

  /** Record that a stimulation actually began. Call only after the device accepts. */
  commit(nowMs = now()): void {
    this.onsets.push(nowMs);
    this.sessionStimuli++;
    this.prune(nowMs);
    this.notify();
  }

  getState(nowMs = now()): SafetyState {
    this.prune(nowMs);
    return {
      armed: this.armed,
      emergencyStopped: this.emergencyStopped,
      armedAt: this.armedAt,
      sessionStimuli: this.sessionStimuli,
      lastStimulusAt: this.onsets[this.onsets.length - 1] ?? null,
      inLastSecond: this.countSince(nowMs - 1000),
      inLastMinute: this.countSince(nowMs - 60_000),
    };
  }

  private countSince(cutoff: number): number {
    let n = 0;
    for (let i = this.onsets.length - 1; i >= 0; i--) {
      if (this.onsets[i] < cutoff) break;
      n++;
    }
    return n;
  }

  private prune(nowMs: number): void {
    const cutoff = nowMs - 60_000;
    while (this.onsets.length > 0 && this.onsets[0] < cutoff) this.onsets.shift();
  }

  private notify(): void {
    const state = this.getState();
    for (const fn of this.listeners) fn(state);
  }
}

function deny(reason: string): GateDecision {
  return { allowed: false, reason };
}
